import React, { useState, useEffect, useRef, useCallback } from 'react';
import { nfConfirm } from '../components/confirm';
import { api } from '../api';

// Страница рассылок (только владелец): отправка массовая / одному по ID,
// до 10 фото одним сообщением (альбом), тулбар Telegram-HTML (Max тоже
// понимает format=html), реестр всех отправок со скачиванием логов.

const MAX_IMAGES = 10;

// Лимиты Telegram на фото. Те же значения проверяет бэкенд (notifications.py) —
// здесь дублируем, чтобы негодный файл не уезжал на сервер: nginx на инстансах
// Стемса режет тело запроса на 10 МБ и отдаёт 413 без внятного объяснения.
const TG_PHOTO_MAX_MB = 10;
const TG_PHOTO_MAX_SUM_SIDES = 10000;   // ширина + высота
const TG_PHOTO_MAX_RATIO = 20;          // соотношение сторон

// Лимиты «одним сообщением»: TG-подпись к фото — 1024 видимых символа
// (длиннее — бот вынужден слать альбом + текст отдельно), TG без фото — 4096,
// Max — 4000. Считаем видимые символы: HTML-теги (<b>, <a href>) не в счёт.
const TG_CAPTION_LIMIT = 1024;
const TG_TEXT_LIMIT = 4096;
const MAX_TEXT_LIMIT = 4000;
const visibleLen = (t) => (t || '').replace(/<[^>]+>/g, '').length;

const KIND_LABELS = { mass: 'Массовая', single: 'Одиночная' };
const STATUS_LABELS = {
  starting: { label: 'Запуск…', color: '#9A4820', bg: '#F9EBCF' },
  sending: { label: 'Отправляется', color: '#9A4820', bg: '#F9EBCF' },
  pausing: { label: 'Ставится на паузу…', color: '#9A4820', bg: '#F9EBCF' },
  paused: { label: 'На паузе', color: '#3A83BB', bg: '#E4F0F9' },
  cancelling: { label: 'Останавливается…', color: '#9A4820', bg: '#F9EBCF' },
  cancelled: { label: 'Остановлена', color: '#5E6561', bg: '#F4F4EF' },
  done: { label: 'Завершена', color: '#1F763B', bg: '#DCF4E0' },
  error: { label: 'Ошибка', color: '#CB4D45', bg: '#FFE6E3' },
  interrupted: { label: 'Прервана', color: '#5E6561', bg: '#F4F4EF' },
};

const ACTIVE_STATUSES = ['starting', 'sending', 'pausing', 'cancelling'];

// Темп массовой рассылки, сообщений в минуту на платформу. За рассылкой идёт волна
// переходов в витрину — темп её растягивает (05.10.2026 волна повесила api Стемса).
const PACE_OPTIONS = [
  { value: '300', label: 'Обычный', hint: '300 в минуту' },
  { value: '120', label: 'Бережный', hint: '120 в минуту — для большой базы' },
  { value: '0', label: 'Без пауз', hint: 'с предельной скоростью бота' },
];

function BroadcastPage({ showToast }) {
  const [textTg, setTextTg] = useState('');
  const [textMax, setTextMax] = useState('');
  const [imageFiles, setImageFiles] = useState([]);       // File[]
  const [photoProblems, setPhotoProblems] = useState([]); // почему фото не приняли
  const [imagePreviews, setImagePreviews] = useState([]); // dataURL[]
  const [platform, setPlatform] = useState('all');
  const [pace, setPace] = useState('300');
  const [mode, setMode] = useState('mass');               // mass | single
  // ID одиночной отправки вводятся вручную КАЖДЫЙ раз — без автоподстановки
  const [singleTgId, setSingleTgId] = useState('');
  const [singleMaxId, setSingleMaxId] = useState('');
  const [sending, setSending] = useState(false);
  const [broadcastId, setBroadcastId] = useState(null);
  const [broadcastStatus, setBroadcastStatus] = useState(null);
  const [pollNonce, setPollNonce] = useState(0);   // перезапуск опроса после «Продолжить»
  const [stats, setStats] = useState({ tg: 0, max: 0 });
  const [activeTab, setActiveTab] = useState('telegram');
  const textareaRef = useRef(null);

  // Реестр
  const [registry, setRegistry] = useState([]);
  const [registryKind, setRegistryKind] = useState('all');
  const [registryLoading, setRegistryLoading] = useState(false);

  useEffect(() => { loadStats(); }, []);

  const loadRegistry = useCallback(async (kind) => {
    setRegistryLoading(true);
    try {
      const data = await api.getBroadcasts(kind, 30, 0);
      setRegistry(data.items || []);
    } catch (e) {
      console.log('Registry load error:', e);
    } finally {
      setRegistryLoading(false);
    }
  }, []);

  useEffect(() => { loadRegistry(registryKind); }, [registryKind, loadRegistry]);

  // Получатели — готовой сводкой: раньше качали всех клиентов целиком (19,7 тыс., 10 МБ), и пока
  // список шёл, на кнопках стояло «Все (0)» (проверка 25.10). Те же условия, что у рассылки на сервере
  const loadStats = async () => {
    try {
      const st = await api.getUsersStats();
      setStats({ tg: st.tg_users || 0, max: st.max_users || 0 });
    } catch (e) {
      console.log('Error loading stats:', e);
    }
  };

  // ── Фото (до 10, одним сообщением-альбомом) ──
  // Лимиты Telegram проверяем прямо в браузере: негодное фото не должно
  // ни грузиться на сервер, ни доходить до отправки. 20.08.2026 PNG 4024x7168
  // на 36 МБ отбивался у КАЖДОГО получателя — рассылка ушла в ошибки целиком.
  const checkPhoto = (file) => new Promise((resolve) => {
    const mb = file.size / 1024 / 1024;
    if (mb > TG_PHOTO_MAX_MB) {
      resolve(`«${file.name}» весит ${mb.toFixed(1)} МБ — Telegram принимает фото до ${TG_PHOTO_MAX_MB} МБ`);
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const w = img.naturalWidth, h = img.naturalHeight;
      if (w + h > TG_PHOTO_MAX_SUM_SIDES) {
        resolve(`«${file.name}» — ${w}×${h} px. Telegram принимает фото, у которого ширина и высота `
          + `в сумме не больше ${TG_PHOTO_MAX_SUM_SIDES} px (сейчас ${w + h}). Уменьшите картинку`);
      } else if (Math.max(w, h) / Math.max(Math.min(w, h), 1) > TG_PHOTO_MAX_RATIO) {
        resolve(`«${file.name}» — ${w}×${h} px, слишком вытянутое: Telegram принимает `
          + `соотношение сторон до ${TG_PHOTO_MAX_RATIO}:1`);
      } else {
        resolve(null);
      }
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(`«${file.name}» не читается как картинка`); };
    img.src = url;
  });

  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const room = MAX_IMAGES - imageFiles.length;
    if (files.length > room) {
      showToast(`Максимум ${MAX_IMAGES} фото — всё уходит одним сообщением`, 'error');
    }
    const accepted = [];
    const problems = [];
    for (const file of files.slice(0, room)) {
      const problem = await checkPhoto(file);
      if (problem) { problems.push(problem); showToast(problem, 'error'); }
      else accepted.push(file);
    }
    setPhotoProblems(problems);   // тост живёт 3 секунды — текст нужен и на виду
    if (!accepted.length) return;
    // Копия в памяти: если файл на диске потом поправят или переместят, браузер обрывает
    // отправку («Failed to fetch»). 23.10 у Букетберри так Max ушёл текстом и фото отдельно
    const copies = await Promise.all(accepted.map(async (f) =>
      new File([await f.arrayBuffer()], f.name, { type: f.type, lastModified: f.lastModified })));
    setImageFiles(prev => [...prev, ...copies]);
    accepted.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreviews(prev => [...prev, ev.target.result]);
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (idx) => {
    setImageFiles(prev => prev.filter((_, i) => i !== idx));
    setImagePreviews(prev => prev.filter((_, i) => i !== idx));
  };

  // ── Тулбар форматирования: оборачивает ВЫДЕЛЕННЫЙ текст тегами ──
  const wrapSelection = (before, after) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const value = activeTab === 'telegram' ? textTg : textMax;
    const setter = activeTab === 'telegram' ? setTextTg : setTextMax;
    const selected = value.slice(start, end) || 'текст';
    const next = value.slice(0, start) + before + selected + after + value.slice(end);
    setter(next);
    // возвращаем фокус и выделение на обёрнутый фрагмент
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(start + before.length, start + before.length + selected.length);
    });
  };

  const insertLink = () => {
    const url = window.prompt('Ссылка (https://…):');
    if (!url) return;
    wrapSelection(`<a href="${url.trim()}">`, '</a>');
  };

  const getRecipientCount = () => {
    if (platform === 'telegram') return stats.tg;
    if (platform === 'max') return stats.max;
    return stats.tg + stats.max;
  };

  const handleSend = async () => {
    if (mode === 'single' && !singleTgId.trim() && !singleMaxId.trim()) {
      showToast('Укажите TG и/или Max ID получателя', 'error');
      return;
    }
    if (!textTg && !textMax && imageFiles.length === 0) {
      showToast('Введите текст или прикрепите фото', 'error');
      return;
    }
    // Лимиты «одним сообщением»: подпись к фото в TG — 1024 видимых символа
    // (иначе бот разбивает на альбом + отдельный текст), без фото — 4096; Max — 4000.
    const tgLimit = imageFiles.length > 0 ? TG_CAPTION_LIMIT : TG_TEXT_LIMIT;
    if (visibleLen(textTg) > tgLimit) {
      showToast(
        imageFiles.length > 0
          ? `Текст Telegram длиннее ${TG_CAPTION_LIMIT} символов — с фото не влезет в одно сообщение. Сократите текст.`
          : `Текст Telegram длиннее ${TG_TEXT_LIMIT} символов.`,
        'error'
      );
      setActiveTab('telegram');
      return;
    }
    if (visibleLen(textMax) > MAX_TEXT_LIMIT) {
      showToast(`Текст Max длиннее ${MAX_TEXT_LIMIT} символов. Сократите текст.`, 'error');
      setActiveTab('max');
      return;
    }
    if (mode === 'mass') {
      const count = getRecipientCount();
      if (count === 0) {
        showToast('Нет получателей', 'error');
        return;
      }
      if (!(await nfConfirm(`Отправить рассылку ${count} пользователям?`, { confirmText: 'Отправить' }))) return;
    }

    setSending(true);
    setBroadcastStatus(null);
    setBroadcastId(null);
    // последняя рассылка до отправки — чтобы при обрыве связи понять, создалась ли новая
    const lastId = (await api.getBroadcasts('all', 1, 0).catch(() => null))?.items?.[0]?.id || 0;
    try {
      const res = await api.sendBroadcast({
        textTg,
        textMax,
        platform,
        imageFiles,
        kind: mode,
        singleTgId: mode === 'single' ? singleTgId.trim() : '',
        singleMaxId: mode === 'single' ? singleMaxId.trim() : '',
        pacePerMin: mode === 'mass' ? pace : '',
      });
      if (res.broadcast_id) {
        setBroadcastId(res.broadcast_id);
        setBroadcastStatus({
          status: 'starting',
          tg_total: res.tg_total || 0,
          max_total: res.max_total || 0,
          tg_sent: 0, tg_failed: 0, max_sent: 0, max_failed: 0,
        });
      }
      loadRegistry(registryKind);
    } catch (e) {
      setSending(false);
      if (e.network) {
        // Связь оборвалась — ответа нет; по реестру видно, создалась ли рассылка (не отправить дважды)
        const fresh = await api.getBroadcasts('all', 1, 0).catch(() => null);
        const created = fresh?.items?.[0] && fresh.items[0].id > lastId;
        showToast(created
          ? 'Связь оборвалась, но рассылка создана — смотрите реестр ниже, повторно не отправляйте'
          : 'Рассылка не ушла: связь с сервером оборвалась. Проверьте интернет и нажмите «Отправить» ещё раз', 'error');
        loadRegistry(registryKind);
        return;
      }
      showToast(e.message || 'Ошибка рассылки', 'error');
    }
  };

  // Polling статуса (из реестра в БД — переживает рестарты).
  // Цепочка setTimeout, а не setInterval: на терминальном статусе просто
  // не планируем следующий опрос — иначе тост о завершении показывался бы
  // заново каждые 4 секунды.
  useEffect(() => {
    if (!broadcastId) return;
    let active = true;
    let timer = null;

    const poll = async () => {
      try {
        const status = await api.getBroadcastStatus(broadcastId);
        if (!active) return;
        setBroadcastStatus(status);
        if (!ACTIVE_STATUSES.includes(status.status)) {
          setSending(false);
          const totalSent = (status.tg_sent || 0) + (status.max_sent || 0);
          const totalFailed = (status.tg_failed || 0) + (status.max_failed || 0);
          showToast(status.status === 'cancelled'
            ? `Рассылка остановлена: успели отправить ${totalSent}`
            : status.status === 'paused'
              ? `Рассылка на паузе: отправлено ${totalSent}`
              : `Отправка завершена: доставлено ${totalSent}, ошибок ${totalFailed}`);
          loadRegistry(registryKind);
          return; // терминальный статус — поллинг закончен
        }
      } catch (e) {
        console.log('Poll error:', e);
      }
      if (active) timer = setTimeout(poll, 4000);
    };

    poll();
    return () => { active = false; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [broadcastId, pollNonce]);

  const downloadLog = async (id, format) => {
    try {
      await api.downloadBroadcastLog(id, format);
    } catch (e) {
      showToast(e.message || 'Лог не найден', 'error');
    }
  };

  const cancelBroadcast = async (id) => {
    if (!(await nfConfirm('Остановить рассылку? Уже отправленные сообщения не отзываются.', { confirmText: 'Остановить' }))) return;
    try {
      await api.cancelBroadcast(id);
      showToast('Останавливаю — рассылка прервётся в течение нескольких секунд');
      // На паузе опрос уже остановлен — запускаем заново, чтобы панель показала итог
      if (id === broadcastId) setPollNonce((n) => n + 1);
      loadRegistry(registryKind);
    } catch (e) {
      showToast(e.message || 'Не удалось остановить', 'error');
    }
  };

  const pauseBroadcast = async (id) => {
    try {
      await api.pauseBroadcast(id);
      showToast('Ставлю на паузу — текущая пачка дошлётся');
      loadRegistry(registryKind);
    } catch (e) {
      showToast(e.message || 'Не удалось поставить на паузу', 'error');
    }
  };

  const resumeBroadcast = async (id) => {
    try {
      await api.resumeBroadcast(id);
      showToast('Продолжаю с того же места — уже получившим повторно не отправится');
      // Прогресс продолженной рассылки — в панели статуса, опрос заново
      setBroadcastId(id);
      setSending(true);
      setPollNonce((n) => n + 1);
      loadRegistry(registryKind);
    } catch (e) {
      showToast(e.message || 'Не удалось продолжить', 'error');
    }
  };

  const currentText = activeTab === 'telegram' ? textTg : textMax;
  const setCurrentText = activeTab === 'telegram' ? setTextTg : setTextMax;

  // Лимит активной вкладки: TG с фото — подпись 1024, без фото — 4096; Max — 4000.
  // Считаем ВИДИМЫЕ символы (теги <b>/<i>/<a> не считаются — TG считает так же).
  const currentLimit = activeTab === 'telegram'
    ? (imageFiles.length > 0 ? TG_CAPTION_LIMIT : TG_TEXT_LIMIT)
    : MAX_TEXT_LIMIT;
  const currentVisibleLen = visibleLen(currentText);
  const overLimit = currentVisibleLen > currentLimit;

  return (
    <div>
      <h1 style={{ marginBottom: '24px' }}>Рассылка</h1>

      <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
        {/* Форма */}
        <div style={{ flex: 1, minWidth: 'min(380px, 100%)' }}>
          <div className="card" style={{ padding: '24px' }}>
            {/* Режим: всем / одному по ID */}
            {/* Переключатели «одно из» — .seg: выбранное не похоже на главную кнопку (UX-аудит 25.10) */}
            <div className="seg" style={{ display: 'flex', width: 'fit-content', marginBottom: '16px' }}>
              <button type="button" onClick={() => setMode('mass')} className={`seg-btn${mode === 'mass' ? ' on' : ''}`}>
                Всем клиентам
              </button>
              <button type="button" onClick={() => setMode('single')} className={`seg-btn${mode === 'single' ? ' on' : ''}`}>
                Одному по ID (тест)
              </button>
            </div>

            {mode === 'single' && (
              <div style={{
                display: 'flex', gap: '12px', marginBottom: '16px',
                padding: '12px', background: '#F4F4EF', '--ch': '6px',
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Telegram ID</div>
                  <input type="text" value={singleTgId} onChange={e => setSingleTgId(e.target.value)}
                    placeholder="напр. 199179820"
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', '--ch': '5px', '--stroke': '#ddd', fontSize: '14px', boxSizing: 'border-box' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Max ID</div>
                  <input type="text" value={singleMaxId} onChange={e => setSingleMaxId(e.target.value)}
                    placeholder="напр. 28575844"
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', '--ch': '5px', '--stroke': '#ddd', fontSize: '14px', boxSizing: 'border-box' }} />
                </div>
              </div>
            )}

            {/* Текст для TG / MAX — переключатель, как остальные «одно из» (UX-аудит 25.10) */}
            <div className="seg" style={{ display: 'flex', width: 'fit-content', marginBottom: '12px' }}>
              <button type="button" className={`seg-btn${activeTab === 'telegram' ? ' on' : ''}`} onClick={() => setActiveTab('telegram')}>Telegram</button>
              <button type="button" className={`seg-btn${activeTab === 'max' ? ' on' : ''}`} onClick={() => setActiveTab('max')}>Max</button>
            </div>

            {/* Тулбар форматирования — обернуть выделенный текст */}
            <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', alignItems: 'center' }}>
              <button type="button" className="btn-icon" style={{ fontWeight: 700 }} title="Жирный (выделите текст)" aria-label="Жирный"
                onClick={() => wrapSelection('<b>', '</b>')}>Ж</button>
              <button type="button" className="btn-icon" style={{ fontStyle: 'italic' }} title="Курсив (выделите текст)" aria-label="Курсив"
                onClick={() => wrapSelection('<i>', '</i>')}>К</button>
              <button type="button" className="btn-icon" style={{ textDecoration: 'line-through' }} title="Зачёркнутый (выделите текст)" aria-label="Зачёркнутый"
                onClick={() => wrapSelection('<s>', '</s>')}>S</button>
              <button type="button" className="btn btn-secondary btn-sm" title="Ссылка на выделенный текст"
                onClick={insertLink}>Ссылка</button>
              <span style={{ fontSize: '11px', color: '#999', marginLeft: '4px' }}>
                выделите текст и нажмите кнопку
              </span>
            </div>

            <textarea
              ref={textareaRef}
              value={currentText}
              onChange={(e) => setCurrentText(e.target.value)}
              placeholder={activeTab === 'telegram' ? 'Текст для Telegram...' : 'Текст для Max...'}
              style={{
                width: '100%', minHeight: '180px', padding: '12px',
                border: overLimit ? '1px solid #CB4D45' : '1px solid #ddd',
                '--ch': '6px', '--stroke': (overLimit ? '#CB4D45' : '#ddd'), fontSize: '14px',
                fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box',
              }}
            />
            {/* Счётчик лимита «одним сообщением» */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              fontSize: '12px', marginTop: '4px',
            }}>
              <span style={{ color: '#999' }}>
                {activeTab === 'telegram' && imageFiles.length > 0
                  ? 'Лимит подписи к фото — текст и фото уйдут одним сообщением'
                  : 'Лимит одного сообщения'}
              </span>
              <span style={{ color: overLimit ? '#CB4D45' : '#999', fontWeight: overLimit ? 700 : 400 }}>
                {currentVisibleLen} / {currentLimit}
              </span>
            </div>
            {overLimit && (
              <div style={{ fontSize: '12px', color: '#CB4D45', marginTop: '2px' }}>
                {activeTab === 'telegram' && imageFiles.length > 0
                  ? 'Текст длиннее лимита — с фото НЕ влезет в одно сообщение. Отправка заблокирована, сократите текст.'
                  : 'Текст длиннее лимита одного сообщения. Отправка заблокирована.'}
              </div>
            )}

            {/* Фото: до 10 одним сообщением */}
            <div style={{ marginTop: '16px' }}>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                {imagePreviews.map((src, idx) => (
                  <div key={idx} style={{ position: 'relative' }}>
                    <img src={src} alt={`Фото ${idx + 1}`} style={{
                      width: '90px', height: '90px', objectFit: 'cover',
                      '--ch': '6px', '--stroke': '#eee', border: '1px solid #eee',
                    }} />
                    <button onClick={() => removeImage(idx)} style={{
                      position: 'absolute', top: '2px', right: '2px',
                      background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none',
                      borderRadius: '50%', width: '20px', height: '20px',
                      cursor: 'pointer', fontSize: '12px', lineHeight: 1,
                    }}>×</button>
                  </div>
                ))}
                {imageFiles.length < MAX_IMAGES && (
                  <label style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: '90px', height: '90px', border: '1px dashed #ccc',
                    '--ch': '6px', '--stroke': '#ccc', cursor: 'pointer', color: '#666',
                    fontSize: '13px', textAlign: 'center',
                  }}>
                    <input type="file" accept="image/*" multiple onChange={handleImageSelect}
                      style={{ display: 'none' }} />
                    + фото
                  </label>
                )}
              </div>
              <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                {imageFiles.length}/{MAX_IMAGES} — все фото уходят одним сообщением (альбомом).
                {' '}Telegram принимает фото до {TG_PHOTO_MAX_MB} МБ, ширина и высота в сумме — до {TG_PHOTO_MAX_SUM_SIDES} px
              </div>
              {photoProblems.length > 0 && (
                <div style={{
                  marginTop: '8px', padding: '8px 12px',
                  background: '#F9EBCF', '--ch': '5px', fontSize: '13px', color: '#9A4820',
                }}>
                  {photoProblems.map((p, i) => <div key={i}>{p}</div>)}
                </div>
              )}
            </div>

            {/* Платформа (только для массовой) */}
            {mode === 'mass' && (
              <div style={{ marginTop: '16px' }}>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Отправить:</div>
                <div className="seg">
                  {['all', 'telegram', 'max'].map(p => (
                    <button key={p} type="button" onClick={() => setPlatform(p)} className={`seg-btn${platform === p ? ' on' : ''}`}>
                      {p === 'all' ? `Все (${stats.tg + stats.max})` :
                       p === 'telegram' ? `Telegram (${stats.tg})` :
                       `Max (${stats.max})`}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Темп (только для массовой) */}
            {mode === 'mass' && (
              <div style={{ marginTop: '16px' }}>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Темп:</div>
                <div className="seg">
                  {PACE_OPTIONS.map(o => (
                    <button key={o.value} type="button" onClick={() => setPace(o.value)} title={o.hint}
                      className={`seg-btn${pace === o.value ? ' on' : ''}`}>
                      {o.label}
                    </button>
                  ))}
                </div>
                <div style={{ fontSize: '12px', color: '#999', marginTop: '6px' }}>
                  {PACE_OPTIONS.find(o => o.value === pace)?.hint}
                  {pace !== '0' && getRecipientCount() > 0 && ` · около ${Math.max(1, Math.round(getRecipientCount() / Number(pace)))} мин на всех`}
                </div>
              </div>
            )}

            {mode === 'mass' && platform === 'all' && (!textTg || !textMax) && (
              <div style={{
                marginTop: '12px', padding: '8px 12px',
                background: '#F9EBCF', '--ch': '5px', fontSize: '13px', color: '#9A4820',
              }}>
                {!textTg && !textMax
                  ? 'Заполните текст для Telegram и Max'
                  : !textTg ? 'Не заполнен текст для Telegram' : 'Не заполнен текст для Max'}
              </div>
            )}

            <div style={{ marginTop: '24px' }}>
              <button
                className="btn btn-primary"
                onClick={handleSend}
                disabled={sending || (!textTg && !textMax && imageFiles.length === 0)}
                style={{ fontSize: '15px', padding: '10px 24px' }}
              >
                {sending ? 'Отправляю...' :
                  mode === 'single' ? 'Отправить одному' : `Отправить рассылку (${getRecipientCount()})`}
              </button>
            </div>

            {/* Статус текущей отправки */}
            {broadcastStatus && (
              <div style={{
                marginTop: '16px', padding: '16px',
                background: (STATUS_LABELS[broadcastStatus.status] || STATUS_LABELS.starting).bg,
                '--ch': '6px', fontSize: '14px',
              }}>
                <div style={{ fontWeight: 600, marginBottom: '8px' }}>
                  {(STATUS_LABELS[broadcastStatus.status] || STATUS_LABELS.starting).label}
                </div>
                {broadcastStatus.tg_total > 0 && (
                  <div style={{ marginBottom: '4px' }}>
                    Telegram: отправлено {broadcastStatus.tg_sent || 0} / {broadcastStatus.tg_total}, ошибок {broadcastStatus.tg_failed || 0}
                  </div>
                )}
                {broadcastStatus.max_total > 0 && (
                  <div>
                    Max: отправлено {broadcastStatus.max_sent || 0} / {broadcastStatus.max_total}, ошибок {broadcastStatus.max_failed || 0}
                  </div>
                )}
                {broadcastStatus.error && (
                  <div style={{ color: '#CB4D45', marginTop: '4px' }}>{broadcastStatus.error}</div>
                )}
                {['starting', 'sending', 'paused'].includes(broadcastStatus.status) && (
                  <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                    {broadcastStatus.status === 'paused' ? (
                      <button className="btn btn-secondary" style={{ fontSize: '12px', padding: '4px 12px' }}
                        onClick={() => resumeBroadcast(broadcastId)}>
                        ▶ Продолжить
                      </button>
                    ) : (
                      <button className="btn btn-secondary" style={{ fontSize: '12px', padding: '4px 12px' }}
                        onClick={() => pauseBroadcast(broadcastId)}>
                        ⏸ Пауза
                      </button>
                    )}
                    <button className="btn btn-secondary"
                      style={{ fontSize: '12px', padding: '4px 12px', color: '#CB4D45', borderColor: '#CB4D45', '--stroke': '#CB4D45' }}
                      onClick={() => cancelBroadcast(broadcastId)}>
                      ⏹ Остановить рассылку
                    </button>
                  </div>
                )}
                {['done', 'error', 'cancelled', 'paused', 'interrupted'].includes(broadcastStatus.status) && broadcastStatus.has_log && (
                  <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                    <button className="btn btn-secondary" style={{ fontSize: '12px', padding: '4px 10px' }}
                      onClick={() => downloadLog(broadcastId, 'jsonl')}>Скачать JSONL</button>
                    <button className="btn btn-secondary" style={{ fontSize: '12px', padding: '4px 10px' }}
                      onClick={() => downloadLog(broadcastId, 'csv')}>Скачать CSV (;)</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Превью */}
        <div style={{ flex: 1, minWidth: 'min(320px, 100%)' }}>
          <div className="card" style={{ padding: '24px' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#666' }}>
              Превью {activeTab === 'telegram' ? 'Telegram' : 'Max'}
            </h3>
            <div style={{
              background: activeTab === 'telegram' ? '#DCF4E0' : '#DEEFFF',
              '--ch': '10px', padding: '12px', maxWidth: '400px',
            }}>
              {imagePreviews.length > 0 && (
                <div style={{
                  display: 'grid', gap: '2px', marginBottom: '8px',
                  gridTemplateColumns: imagePreviews.length === 1 ? '1fr' : '1fr 1fr',
                }}>
                  {imagePreviews.map((src, i) => (
                    <img key={i} src={src} alt="" style={{
                      width: '100%', aspectRatio: imagePreviews.length === 1 ? 'auto' : '1',
                      objectFit: 'cover', '--ch': '5px',
                    }} />
                  ))}
                </div>
              )}
              {currentText ? (
                <div
                  style={{ fontSize: '14px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                  dangerouslySetInnerHTML={{ __html: currentText }}
                />
              ) : (
                <div style={{ fontSize: '14px', color: '#999' }}>Текст сообщения...</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Реестр отправок ── */}
      <div className="card" style={{ padding: '24px', marginTop: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
          <h3 style={{ margin: 0, fontSize: '16px' }}>Реестр отправок</h3>
          <div style={{ display: 'flex', gap: '6px' }}>
            {[['all', 'Все'], ['mass', 'Массовые'], ['single', 'Одиночные']].map(([k, label]) => (
              <button key={k} onClick={() => setRegistryKind(k)}
                className={registryKind === k ? 'btn btn-primary' : 'btn btn-secondary'}
                style={{ fontSize: '12px', padding: '4px 12px' }}>
                {label}
              </button>
            ))}
            <button className="btn btn-secondary" style={{ fontSize: '12px', padding: '4px 12px' }}
              onClick={() => loadRegistry(registryKind)}>↻</button>
          </div>
        </div>

        {registryLoading && registry.length === 0 ? (
          <div style={{ color: '#999', padding: '16px', textAlign: 'center' }}>Загрузка…</div>
        ) : registry.length === 0 ? (
          <div style={{ color: '#999', padding: '16px', textAlign: 'center' }}>Отправок пока не было</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#888', borderBottom: '1px solid #eee' }}>
                  <th style={{ padding: '6px 8px' }}>#</th>
                  <th style={{ padding: '6px 8px' }}>Дата</th>
                  <th style={{ padding: '6px 8px' }}>Тип</th>
                  <th style={{ padding: '6px 8px' }}>Текст</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center' }}>Фото</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center' }}>TG ✓/✗</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center' }}>Max ✓/✗</th>
                  <th style={{ padding: '6px 8px' }}>Статус</th>
                  <th style={{ padding: '6px 8px' }}>Лог</th>
                </tr>
              </thead>
              <tbody>
                {registry.map(b => {
                  const st = STATUS_LABELS[b.status] || STATUS_LABELS.starting;
                  const snippet = (b.text_tg || b.text_max || '').replace(/<[^>]+>/g, '').slice(0, 60);
                  return (
                    <tr key={b.id} style={{ borderBottom: '1px solid #f2f2f0' }}>
                      <td style={{ padding: '6px 8px', color: '#999' }}>{b.id}</td>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                        {b.created_at ? new Date(b.created_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <span style={{
                          padding: '2px 8px', '--ch': '4px', fontSize: '11px', fontWeight: 600,
                          background: b.kind === 'mass' ? '#DFF2E8' : '#F9EBCF',
                          color: b.kind === 'mass' ? '#1C3D2E' : '#7E5D00',
                        }}>
                          {KIND_LABELS[b.kind] || b.kind}
                        </span>
                      </td>
                      <td style={{ padding: '6px 8px', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={snippet}>
                        {snippet || <span style={{ color: '#ccc' }}>— только фото —</span>}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'center' }}>{b.image_count || '—'}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                        {b.tg_total > 0 ? <><span style={{ color: '#1F763B' }}>{b.tg_sent}</span>/<span style={{ color: b.tg_failed ? '#CB4D45' : '#999' }}>{b.tg_failed}</span></> : '—'}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                        {b.max_total > 0 ? <><span style={{ color: '#1F763B' }}>{b.max_sent}</span>/<span style={{ color: b.max_failed ? '#CB4D45' : '#999' }}>{b.max_failed}</span></> : '—'}
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <span style={{ padding: '2px 8px', '--ch': '4px', fontSize: '11px', fontWeight: 600, background: st.bg, color: st.color }}>
                          {st.label}
                        </span>
                      </td>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                        {/* Действия строки — классами кнопок, без синих ссылок и эмодзи (UX-аудит 25.10) */}
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        {['starting', 'sending'].includes(b.status) && (
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => pauseBroadcast(b.id)} title="Поставить на паузу">
                            Пауза
                          </button>
                        )}
                        {b.status === 'paused' && (
                          <button type="button" className="btn btn-primary btn-sm" onClick={() => resumeBroadcast(b.id)} title="Продолжить с того же места">
                            Продолжить
                          </button>
                        )}
                        {['starting', 'sending', 'paused'].includes(b.status) && (
                          <button type="button" className="btn btn-danger-outline btn-sm" onClick={() => cancelBroadcast(b.id)} title="Остановить рассылку">
                            Стоп
                          </button>
                        )}
                        {b.has_log ? (
                          <>
                            <button type="button" className="btn-link" style={{ fontSize: 12 }} onClick={() => downloadLog(b.id, 'jsonl')} title="JSONL">
                              JSONL
                            </button>
                            <button type="button" className="btn-link" style={{ fontSize: 12 }} onClick={() => downloadLog(b.id, 'csv')} title="CSV с разделителем ;">
                              CSV
                            </button>
                          </>
                        ) : (!ACTIVE_STATUSES.includes(b.status) && b.status !== 'paused' && <span style={{ color: '#ccc' }}>—</span>)}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default BroadcastPage;
